import { useCallback, useEffect, useRef, useState } from 'react';
import './App.css';
import AdminAuthPage from './AdminAuthPage';
import AdminPanel from './AdminPanel';
import BackgammonGame from './BackgammonGame';
import SplashScreen from './SplashScreen';
import AuthPage from './AuthPage';
import OnlineLobby from './OnlineLobby';
import OnlineMatchGame from './OnlineMatchGame';
import ProfilePage from './ProfilePage';
import PlayerTransactionsPage from './PlayerTransactionsPage';
import TournamentLobby from './TournamentLobby';
import TournamentLivePage from './TournamentLivePage';
import api, { setAuthToken } from './api';
import SupportWidget from './SupportWidget';
import { clearGoogleRedirectPending, consumeGoogleRedirectResult, wasGoogleRedirectPending } from './firebase';


const NATIVE_TOKEN_KEY = 'bg_token';
const NATIVE_USER_KEY = 'bg_user';
const EXTERNAL_TOKEN_KEY = 'bg_external_token';
const EXTERNAL_USER_KEY = 'bg_external_user';
const ADMIN_TOKEN_KEY = 'bg_admin_token';
const ADMIN_USER_KEY = 'bg_admin_user';
const LAST_AUTH_ERROR_KEY = 'bg_last_auth_error';



function isStaffUser(user) {
  return user?.role === 'superadmin' || user?.role === 'agent' || user?.isAdmin;
}

function readStoredSession() {
  const externalToken = sessionStorage.getItem(EXTERNAL_TOKEN_KEY);
  const externalUser = sessionStorage.getItem(EXTERNAL_USER_KEY);
  if (externalToken && externalUser) {
    return {
      token: externalToken,
      user: JSON.parse(externalUser),
    };
  }

  const nativeToken = localStorage.getItem(NATIVE_TOKEN_KEY);
  const nativeUser = localStorage.getItem(NATIVE_USER_KEY);
  return {
    token: nativeToken || '',
    user: nativeUser ? JSON.parse(nativeUser) : null,
  };
}

function clearAllSessions() {
  localStorage.removeItem(NATIVE_TOKEN_KEY);
  localStorage.removeItem(NATIVE_USER_KEY);
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(ADMIN_USER_KEY);
  sessionStorage.removeItem(EXTERNAL_TOKEN_KEY);
  sessionStorage.removeItem(EXTERNAL_USER_KEY);
}

function persistSession(authToken, authUser) {
  clearAllSessions();

  if (authUser?.isExternal) {
    sessionStorage.setItem(EXTERNAL_TOKEN_KEY, authToken);
    sessionStorage.setItem(EXTERNAL_USER_KEY, JSON.stringify(authUser));
    return;
  }

  localStorage.setItem(NATIVE_TOKEN_KEY, authToken);
  localStorage.setItem(NATIVE_USER_KEY, JSON.stringify(authUser));
}

function readStoredAdminSession() {
  const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
  const adminUser = localStorage.getItem(ADMIN_USER_KEY);
  return {
    token: adminToken || '',
    user: adminUser ? JSON.parse(adminUser) : null,
  };
}

function persistAdminSession(authToken, authUser) {
  clearAllSessions();
  localStorage.setItem(ADMIN_TOKEN_KEY, authToken);
  localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(authUser));
}

function buildExternalPayloadFromUrl() {
  const params = new URLSearchParams(window.location.search);
  if (!params.get('platform') || !params.get('externalUserId') || !params.get('name')) {
    return null;
  }

  return {
    platform: params.get('platform') || '',
    externalUserId: params.get('externalUserId') || '',
    name: params.get('name') || '',
    email: params.get('email') || '',
    coins: Number(params.get('coins') || 0),
    returnUrl: params.get('returnUrl') || '',
    signature: params.get('signature') || '',
  };
}

function sendExternalResult(user, payload = {}) {
  const returnUrl = user?.returnUrl;
  if (!user?.isExternal || !returnUrl) return;

  const url = new URL(returnUrl);
  const params = url.searchParams;
  params.set('platform', user.platform || '');
  params.set('externalUserId', user.externalUserId || '');
  params.set('playerName', user.name || '');
  Object.entries(payload).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return;
    params.set(key, String(value));
  });
  window.location.assign(url.toString());
}

function getScreenFromPath(pathname, fallbackScreen = 'game') {
  if (pathname === '/online' || pathname.startsWith('/online/')) return 'online';
  if (/^\/tournament\/[^/]+\/live$/.test(pathname)) return 'tournament-live';
  if (pathname === '/tournament' || pathname.startsWith('/tournament/')) return 'tournament';
  return fallbackScreen;
}

function getTournamentIdFromPath(pathname) {
  const match = pathname.match(/^\/tournament\/([^/]+)\/live$/);
  return match ? match[1] : '';
}

function getAdminRouteFromLocation(pathname, historyState) {
  const detailMatch = pathname.match(/^\/admin\/tournamentDetail\/([^/]+)(?:\/(overview|players|matches|subMatches|leaderboard|rounds))?(?:\/([^/]+))?$/);
  if (detailMatch) {
    const [, tournamentId, sectionPart, extraPart] = detailMatch;
    const section = sectionPart || historyState?.section || 'overview';
    return {
      view: 'tournamentDetails',
      tournamentId,
      section,
      participantId: section === 'players' ? (extraPart || historyState?.participantId || '') : '',
      roundNumber: ['overview', 'matches', 'subMatches', 'leaderboard', 'rounds', 'players'].includes(section)
        ? (extraPart || historyState?.roundNumber || '')
        : '',
    };
  }

  return {
    view: historyState?.adminView || 'dashboard',
    tournamentId: historyState?.tournamentId || '',
  };
}

function App() {
  const isAdminRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');
  const stored = isAdminRoute ? readStoredAdminSession() : readStoredSession();
  const initialScreen = isAdminRoute
    ? 'admin'
    : getScreenFromPath(window.location.pathname, stored.user?.isExternal ? 'online' : 'game');
  const [token, setToken] = useState(stored.token);
  const [user, setUser] = useState(stored.user);
  const [isGuest, setIsGuest] = useState(false);
  const [screen, setScreen] = useState(initialScreen);
  const [matchInfo, setMatchInfo] = useState(null);
  const [ready, setReady] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [gamePhase, setGamePhase] = useState('modeSelect');
  const menuRef = useRef(null);
  const googleRedirectHandledRef = useRef(false);
  const [isFullscreen, setIsFullscreen] = useState(Boolean(document.fullscreenElement));
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1280));
  const [adminLocation, setAdminLocation] = useState(() => ({
    pathname: typeof window !== 'undefined' ? window.location.pathname : '/admin',
    historyState: typeof window !== 'undefined' ? window.history.state : null,
  }));

  const navigateToScreen = useCallback((nextScreen, { replace = false } = {}) => {
    setScreen(nextScreen);
    if (isAdminRoute || typeof window === 'undefined') return;
    const nextPath = nextScreen === 'online'
      ? '/online'
      : nextScreen === 'tournament'
        ? '/tournament'
        : nextScreen.startsWith('tournament-live:')
          ? `/tournament/${nextScreen.split(':')[1]}/live`
          : '/';
    const currentPath = window.location.pathname;
    if (currentPath === nextPath) return;
    const method = replace ? 'replaceState' : 'pushState';
    window.history[method]({}, document.title, nextPath);
  }, [isAdminRoute]);

  function navigateAdmin(to, { replace = false, state = {} } = {}) {
    if (typeof window === 'undefined') return;
    const method = replace ? 'replaceState' : 'pushState';
    window.history[method](state, document.title, to);
    setAdminLocation({
      pathname: to,
      historyState: state,
    });
  }

  async function restoreActiveMatchSession() {
    try {
      const res = await api.get('/matches/active');
      const activeMatch = res.data?.data?.activeMatch || null;
      setMatchInfo(activeMatch ? { ...activeMatch, resumed: true } : null);
      if (activeMatch) {
        setScreen('match');
        return true;
      }
      return false;
    } catch (_err) {
      setMatchInfo(null);
      return false;
    }
  }

  useEffect(() => {
    if (isAdminRoute) return;
    if (googleRedirectHandledRef.current) return;
    googleRedirectHandledRef.current = true;

    function formatGoogleAuthError(err) {
      const backendMessage = err?.response?.data?.message;
      if (backendMessage) return backendMessage;

      const firebaseCode = err?.code ? String(err.code) : '';
      const firebaseMessage = err?.message ? String(err.message) : '';
      if (firebaseCode && firebaseMessage) return `${firebaseCode}: ${firebaseMessage}`;
      if (firebaseCode) return firebaseCode;
      if (firebaseMessage) return firebaseMessage;
      return 'Google sign-in failed';
    }

    async function resumeFirebaseGoogleRedirect() {
      try {
        const hadPendingRedirect = wasGoogleRedirectPending();
        const idToken = await consumeGoogleRedirectResult();
        if (!idToken) {
          if (hadPendingRedirect) {
            clearGoogleRedirectPending();
            try {
              sessionStorage.setItem(
                LAST_AUTH_ERROR_KEY,
                'Google sign-in redirect did not complete. This is commonly caused by third-party cookie restrictions because Firebase redirect uses your `authDomain` (firebaseapp.com/web.app) as a third-party site on localhost. Try the popup flow, or allow third-party cookies for `tavlapro-203b2.firebaseapp.com`, and ensure Firebase Authorized Domains includes `localhost`.'
              );
            } catch (_storageErr) {
              // ignore
            }
            setShowAuthModal(true);
          }
          return;
        }

        const res = await api.post('/auth/firebase/google', { idToken });
        handleAuthSuccess(res.data.data);
        setShowAuthModal(false);
      } catch (err) {
        try {
          sessionStorage.setItem(LAST_AUTH_ERROR_KEY, formatGoogleAuthError(err));
        } catch (_storageErr) {
          // ignore
        }
        setShowAuthModal(true);
        // eslint-disable-next-line no-console
        console.error('Google redirect sign-in failed:', err);
      }
    }

    resumeFirebaseGoogleRedirect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminRoute]);

  useEffect(() => {
    async function bootstrapAuth() {
      if (isAdminRoute) {
        if (!token) {
          setAuthToken(null);
          setReady(true);
          return;
        }

        setAuthToken(token);
        try {
          const res = await api.get('/auth/me');
          const me = res.data.data.user;
          if (!isStaffUser(me)) {
            throw new Error('Staff access only');
          }
          setUser(me);
          persistAdminSession(token, me);
        } catch (_err) {
          setToken('');
          setUser(null);
          setAuthToken(null);
          clearAllSessions();
        } finally {
          setReady(true);
        }
        return;
      }

      const externalPayload = buildExternalPayloadFromUrl();
      if (externalPayload) {
        try {
          const res = await api.post('/auth/external/session', externalPayload);
          const authToken = res.data.data.token;
          const authUser = res.data.data.user;
          setToken(authToken);
          setUser(authUser);
          setAuthToken(authToken);
          persistSession(authToken, authUser);
          window.history.replaceState({}, document.title, window.location.pathname);
          const resumed = await restoreActiveMatchSession();
          if (!resumed) {
            navigateToScreen('online', { replace: true });
          }
          setReady(true);
          return;
        } catch (_err) {
          setAuthToken(null);
          setReady(true);
          return;
        }
      }

      if (!token) {
        // No token: auto-set as guest
        setAuthToken(null);
        setIsGuest(true);
        setUser({
          name: 'Guest',
          coins: 0,
          type: 'guest',
        });
        setShowAuthModal(true);
        setReady(true);
        return;
      }

      setAuthToken(token);
      try {
        const res = await api.get('/auth/me');
        const me = res.data.data.user;
        setUser(me);
        persistSession(token, me);
        const resumed = await restoreActiveMatchSession();
        if (!resumed) {
          navigateToScreen(getScreenFromPath(window.location.pathname, me?.isExternal ? 'online' : 'game'), { replace: true });
        }
      } catch (_err) {
        setToken('');
        setUser(null);
        setAuthToken(null);
        clearAllSessions();
      } finally {
        setReady(true);
      }
    }

    bootstrapAuth();
  }, [isAdminRoute, navigateToScreen, token, user?.type]);

  useEffect(() => {
    // splash dismiss is handled by SplashScreen (min display time)
    return undefined;
  }, [ready]);

  function handleAuthSuccess({ token: authToken, user: authUser }) {
    setIsGuest(false);
    setToken(authToken);
    setUser(authUser);
    navigateToScreen(getScreenFromPath(window.location.pathname, authUser?.isExternal ? 'online' : 'game'), { replace: true });
    setAuthToken(authToken);
    persistSession(authToken, authUser);
  }

  function handleAdminAuthSuccess({ token: authToken, user: authUser }) {
    setToken(authToken);
    setUser(authUser);
    setScreen('admin');
    setAuthToken(authToken);
    persistAdminSession(authToken, authUser);
  }

  function handleContinueAsGuest() {
    setIsGuest(true);
    setToken('');
    setUser({
      name: 'Guest',
      coins: 0,
      type: 'guest',
    });
    navigateToScreen('game', { replace: true });
    setMatchInfo(null);
    setMenuOpen(false);
    setAuthToken(null);
  }

  async function refreshCurrentUser() {
    if (!token || isGuest) return;
    try {
      const res = await api.get('/auth/me');
      const me = res.data.data.user;
      setUser(me);
      persistSession(token, me);
    } catch (_err) {
      // ignore
    }
  }

  function handleLogout() {
    setIsGuest(false);
    setToken('');
    setUser(null);
    navigateToScreen(isAdminRoute ? 'admin' : 'game', { replace: true });
    setMatchInfo(null);
    setMenuOpen(false);
    setAuthToken(null);
    clearAllSessions();
  }

  function handleReturnToPlatform(payload = {}) {
    if (user?.isExternal) {
      sendExternalResult(user, payload);
      return;
    }
    handleLogout();
  }

  async function handleMatchCompleted(payload) {
    try {
      await api.post('/matches/report', payload);
    } catch (_err) {
      // best effort only
    }
  }

  useEffect(() => {
    if (!menuOpen) return undefined;
    function onDocClick(e) {
      if (!menuRef.current?.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  useEffect(() => {
    function onFullscreenChange() {
      setIsFullscreen(Boolean(document.fullscreenElement));
    }
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  useEffect(() => {
    function onResize() {
      setViewportWidth(window.innerWidth);
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    function handlePopState() {
      if (isAdminRoute) {
        setAdminLocation({
          pathname: window.location.pathname,
          historyState: window.history.state,
        });
        setScreen('admin');
        return;
      }
      const fallbackScreen = user?.isExternal ? 'online' : 'game';
      setScreen(getScreenFromPath(window.location.pathname, fallbackScreen));
    }

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isAdminRoute, user]);

  useEffect(() => {
    if (!isGuest) return;
    if (screen === 'tournament' || screen.startsWith('tournament-live')) {
      navigateToScreen('game', { replace: true });
    }
  }, [isGuest, navigateToScreen, screen]);

  async function handleFullscreenToggle() {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (_err) {
      // ignore
    }
  }

  const isMobileViewport = viewportWidth < 768;

  function renderFullscreenButton(mode = 'standalone') {
    const isTopbar = mode === 'topbar';
    const isIcon = mode === 'icon';
    const buttonStyle = isTopbar
      ? {
          border: '1px solid #d7b16a',
          background: '#7a4c22',
          color: '#f6e7c1',
          fontWeight: 700,
          padding: isMobileViewport ? '8px 10px' : '10px 12px',
          borderRadius: 10,
          cursor: 'pointer',
          boxShadow: '0 4px 14px rgba(0,0,0,0.45)',
          fontSize: isMobileViewport ? 13 : 'inherit',
        }
      : isIcon
        ? {
            width: 40,
            height: 40,
            borderRadius: 10,
            border: '1px solid #d7b16a',
            background: '#7a4c22',
            color: '#f6e7c1',
            fontWeight: 800,
            fontSize: 19,
            lineHeight: 1,
            cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(0,0,0,0.45)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }
      : {
          position: 'fixed',
          left: isMobileViewport ? 12 : 14,
          right: 'auto',
          bottom: isMobileViewport ? 12 : 14,
          top: 'auto',
          zIndex: 2100,
          border: '1px solid #d7b16a',
          background: '#7a4c22',
          color: '#f6e7c1',
          fontWeight: 700,
          padding: isMobileViewport ? '8px 10px' : '10px 12px',
          borderRadius: 10,
          cursor: 'pointer',
          boxShadow: '0 4px 14px rgba(0,0,0,0.45)',
          fontSize: isMobileViewport ? 13 : 'inherit',
        };

    return (
      <button
        type="button"
        onClick={handleFullscreenToggle}
        title={isFullscreen ? 'Exit Full Screen' : 'Play Full Screen'}
        style={buttonStyle}
      >
        {isIcon ? '⛶' : (isFullscreen ? 'Exit Full Screen' : 'Play Full Screen')}
      </button>
    );
  }

  if (!ready) return <SplashScreen visible={showSplash} onFinish={() => setShowSplash(false)} />;
  if (isAdminRoute) {
    if (!token || !isStaffUser(user)) {
      return <AdminAuthPage onAuthSuccess={handleAdminAuthSuccess} />;
    }
    return (
      <AdminPanel
        user={user}
        onLogout={handleLogout}
        routeInfo={getAdminRouteFromLocation(adminLocation.pathname, adminLocation.historyState)}
        onNavigate={(next) => {
          if (next?.view === 'tournamentDetails' && next?.tournamentId) {
            let nextPath = `/admin/tournamentDetail/${next.tournamentId}`;
            if (next?.section === 'players') {
              nextPath += next?.roundNumber ? `/players/${next.roundNumber}` : '/players';
            } else if (next?.section === 'matches') {
              nextPath += next?.roundNumber ? `/matches/${next.roundNumber}` : '/matches';
            } else if (next?.section === 'subMatches') {
              nextPath += next?.roundNumber ? `/subMatches/${next.roundNumber}` : '/subMatches';
            } else if (next?.section === 'leaderboard') {
              nextPath += next?.roundNumber ? `/leaderboard/${next.roundNumber}` : '/leaderboard';
            } else if (next?.section === 'overview') {
              nextPath += next?.roundNumber ? `/overview/${next.roundNumber}` : '';
            } else if (next?.section === 'rounds') {
              nextPath += next?.roundNumber ? `/rounds/${next.roundNumber}` : '/rounds';
            }
            navigateAdmin(nextPath, {
              replace: false,
              state: {
                adminView: 'tournamentDetails',
                tournamentId: next.tournamentId,
                section: next.section || 'overview',
                participantId: next.participantId || '',
                roundNumber: next.roundNumber || '',
              },
            });
            return;
          }

          navigateAdmin('/admin', {
            replace: true,
            state: {
              adminView: next?.view || 'dashboard',
              tournamentId: next?.tournamentId || '',
            },
          });
        }}
      />
    );
  }

  let content = null;

  if (screen === 'profile') {
    content = (
      <ProfilePage
        user={user}
        onBack={() => navigateToScreen(getScreenFromPath(window.location.pathname, user?.isExternal ? 'online' : 'game'))}
        onWalletChange={refreshCurrentUser}
      />
    );
  } else if (screen === 'transactions') {
    content = (
      <PlayerTransactionsPage
        user={user}
        onBack={() => navigateToScreen(getScreenFromPath(window.location.pathname, user?.isExternal ? 'online' : 'game'))}
      />
    );
  } else if (screen === 'tournament') {
    content = (
      <TournamentLobby
        user={user}
        onWalletChange={refreshCurrentUser}
        onBack={() => navigateToScreen(user?.isExternal ? 'online' : 'game')}
        onOpenTournamentLive={(tournamentId) => navigateToScreen(`tournament-live:${tournamentId}`)}
      />
    );
  } else if (screen.startsWith('tournament-live')) {
    const tournamentId = screen.split(':')[1] || getTournamentIdFromPath(window.location.pathname);
    content = (
      <TournamentLivePage
        tournamentId={tournamentId}
        token={token}
        user={user}
        onBack={() => navigateToScreen('tournament')}
        onMatchStart={(payload) => {
          setMatchInfo(payload);
          setScreen('match');
        }}
      />
    );
  } else if (screen === 'match' && matchInfo) {
    content = (
      <OnlineMatchGame
        token={token}
        user={user}
        matchInfo={matchInfo}
        isFullscreen={isFullscreen}
        onToggleFullscreen={handleFullscreenToggle}
        onMatchSwitch={(payload) => {
          setMatchInfo(payload);
          setScreen('match');
          refreshCurrentUser();
        }}
        onExitMatch={(payload) => {
          setMatchInfo(null);
          if (user?.isExternal && payload?.returnToPlatform) {
            handleReturnToPlatform(payload);
            return;
          }
          if (matchInfo?.tournament?.tournamentId) {
            navigateToScreen(`tournament-live:${matchInfo.tournament.tournamentId}`);
          } else {
            navigateToScreen('online');
          }
          refreshCurrentUser();
        }}
        onWalletChange={refreshCurrentUser}
      />
    );
  } else if (screen === 'online') {
    content = (
          <OnlineLobby
            token={token}
            user={user}
            onBackToGame={() => {
              if (user?.isExternal) {
                handleReturnToPlatform({ status: 'cancelled' });
                return;
              }
              navigateToScreen('game');
            }}
            onOpenHistory={() => setScreen('profile')}
            onMatchStart={(payload) => {
              setMatchInfo(payload);
              setScreen('match');
            }}
            onWalletChange={refreshCurrentUser}
          />
    );
  } else {
    content = (
      <BackgammonGame
        onSelectOnlineMode={() => navigateToScreen('online')}
        onSelectTournamentMode={() => navigateToScreen('tournament')}
        onMatchCompleted={handleMatchCompleted}
        allowTournamentMode={!isGuest}
        allowOnlineMode={!isGuest}
        onlineModeBlockedMessage={isStaffUser(user) ? 'Agent or Admin accounts cannot open the lobby.Use your player ID to play online.  ': ''}
        onExit={null}
        onGamePhaseChange={setGamePhase}
        isFullscreen={isFullscreen}
        onToggleFullscreen={handleFullscreenToggle}
      />
    );
  }

  const showGameFullscreen = screen === 'game' && gamePhase === 'playing';
  const isGuestGame = isGuest && screen === 'game';
  const isGuestMobileGame = isGuest && isMobileViewport && screen === 'game';
  const isAuthedMobileTop = !isGuest && isMobileViewport;
  const showGlobalTopBar = !['match', 'profile', 'transactions'].includes(screen)
    && !screen.startsWith('tournament-live')
    && !(isMobileViewport && screen === 'tournament')
    && (!isGuestMobileGame || gamePhase === 'modeSelect')
    && (!isGuest || screen === 'game');
  const showStandaloneFullscreen = false;
  const shouldDropMenuDown = !isMobileViewport || isGuestMobileGame || isAuthedMobileTop;

  return (
    <>
      <SplashScreen visible={showSplash} onFinish={() => setShowSplash(false)} />
      {content}
      {!isAdminRoute && screen === 'game' && gamePhase === 'modeSelect' && <SupportWidget token={token} bottomOffset={16} />}
      {showStandaloneFullscreen && renderFullscreenButton('standalone')}
      {showAuthModal && isGuest && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 3000,
          }}
          onClick={() => setShowAuthModal(false)}
        >
          <div
            style={{
              background: 'transparent',
              borderRadius: 0,
              width: '100%',
              maxWidth: '100%',
              minHeight: '100vh',
              position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowAuthModal(false)}
              style={{
                position: 'absolute',
                top: 10,
                right: 10,
                border: 'none',
                background: 'transparent',
                fontSize: 24,
                cursor: 'pointer',
                zIndex: 3001,
              }}
            >
              ×
            </button>
            <AuthPage
              onAuthSuccess={(data) => {
                handleAuthSuccess(data);
                setShowAuthModal(false);
              }}
              onContinueAsGuest={() => setShowAuthModal(false)}
            />
          </div>
        </div>
      )}
      {showGlobalTopBar && (
        <div
          style={{
            position: 'fixed',
            top: (isGuestMobileGame || isAuthedMobileTop) ? 14 : (isMobileViewport ? 'auto' : 14),
            right: (isGuestMobileGame || isAuthedMobileTop) ? 14 : (isMobileViewport ? 12 : 14),
            bottom: (isGuestMobileGame || isAuthedMobileTop) ? 'auto' : (isMobileViewport ? 12 : 'auto'),
            left: (isGuestMobileGame || isAuthedMobileTop) ? 'auto' : (isMobileViewport ? 12 : 'auto'),
            zIndex: 2100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: (isAuthedMobileTop || isGuestMobileGame || !isMobileViewport) ? 'flex-end' : 'space-between',
            flexWrap: isAuthedMobileTop ? 'nowrap' : (isMobileViewport ? 'wrap' : 'nowrap'),
            gap: 8,
            maxWidth: isMobileViewport ? 'calc(100vw - 28px)' : 'none',
          }}
        >
          {showGameFullscreen && ((!isGuest && renderFullscreenButton('topbar')) || (isGuestMobileGame && renderFullscreenButton('icon')))}
          {!isGuest && (
            <div
              style={{
                border: '1px solid rgba(215,177,106,0.9)',
                background: 'rgba(33,18,11,0.95)',
                color: '#f6e7c1',
                padding: isMobileViewport ? '8px 10px' : '10px 12px',
                borderRadius: 10,
                fontWeight: 700,
                boxShadow: '0 4px 14px rgba(0,0,0,0.45)',
                fontSize: isMobileViewport ? 13 : 'inherit',
              }}
            >
              Coins: {user?.coins ?? 0}
            </div>
          )}
          {isGuest ? (
            <button
              type="button"
              onClick={() => {
                if (isGuestGame) {
                  setShowAuthModal(true);
                  return;
                }
                setShowAuthModal(true);
              }}
              style={{
                border: '1px solid #d7b16a',
                background: '#7a4c22',
                color: '#f6e7c1',
                padding: isMobileViewport ? '8px 10px' : '10px 12px',
                borderRadius: 10,
                cursor: 'pointer',
                fontWeight: 700,
                boxShadow: '0 4px 14px rgba(0,0,0,0.45)',
                fontSize: isMobileViewport ? 13 : 'inherit',
              }}
            >
              {isGuestGame ? 'Back' : 'Login'}
            </button>
          ) : user?.isExternal ? (
            <>
              <button
                type="button"
                onClick={() => setScreen('profile')}
                style={{
                  border: '1px solid #d7b16a',
                  background: '#3a2314',
                  color: '#f6e7c1',
                  padding: isMobileViewport ? '8px 10px' : '10px 12px',
                  borderRadius: 10,
                  cursor: 'pointer',
                  fontWeight: 700,
                  boxShadow: '0 4px 14px rgba(0,0,0,0.45)',
                  fontSize: isMobileViewport ? 13 : 'inherit',
                }}
              >
                Records
              </button>
              <button
                type="button"
                onClick={() => handleReturnToPlatform({ status: 'cancelled' })}
                style={{
                  border: '1px solid #d7b16a',
                  background: '#643030',
                  color: '#f6e7c1',
                  padding: isMobileViewport ? '8px 10px' : '10px 12px',
                  borderRadius: 10,
                  cursor: 'pointer',
                  fontWeight: 700,
                  boxShadow: '0 4px 14px rgba(0,0,0,0.45)',
                  fontSize: isMobileViewport ? 13 : 'inherit',
                }}
              >
                Return
              </button>
            </>
          ) : (
            <div ref={menuRef} style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setMenuOpen((prev) => !prev)}
                title="Profile"
                style={{
                  width: isMobileViewport ? 40 : 44,
                  height: isMobileViewport ? 40 : 44,
                  borderRadius: '50%',
                  border: '1px solid rgba(215,177,106,0.9)',
                  background: '#2f1a10',
                  color: '#f6e7c1',
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(0,0,0,0.45)',
                }}
              >
                {(user?.name?.[0] || 'U').toUpperCase()}
              </button>

              {menuOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: shouldDropMenuDown ? 52 : 'auto',
                    bottom: shouldDropMenuDown ? 'auto' : 52,
                    right: 0,
                    width: 220,
                    background: 'rgba(22,12,8,0.96)',
                    border: '1px solid rgba(212,165,88,0.45)',
                    borderRadius: 10,
                    padding: 10,
                    color: '#f6e7c1',
                    boxShadow: '0 8px 22px rgba(0,0,0,0.45)',
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: 3 }}>{user.name}</div>
                  <div style={{ fontSize: 12, color: '#d8c39a', marginBottom: 10 }}>{user.email}</div>
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      setScreen('profile');
                    }}
                    style={{
                      width: '100%',
                      border: '1px solid #d7b16a',
                      background: '#7a4c22',
                      color: '#f6e7c1',
                      padding: '8px 10px',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontWeight: 700,
                      marginBottom: 8,
                    }}
                  >
                    Profile
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      setScreen('transactions');
                    }}
                    style={{
                      width: '100%',
                      border: '1px solid #d7b16a',
                      background: '#3a2314',
                      color: '#f6e7c1',
                      padding: '8px 10px',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontWeight: 700,
                      marginBottom: 8,
                    }}
                  >
                    Transactions
                  </button>
                  <button
                    type="button"
                    onClick={handleLogout}
                    style={{
                      width: '100%',
                      border: '1px solid #d7b16a',
                      background: '#643030',
                      color: '#f6e7c1',
                      padding: '8px 10px',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontWeight: 700,
                    }}
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default App;
